"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.assertNonEmptyIdentifier = assertNonEmptyIdentifier;
exports.createServerDatabaseRowShardDocId = createServerDatabaseRowShardDocId;
exports.createServerDatabaseRootDocId = createServerDatabaseRootDocId;
exports.collectDatabaseStorageSyncDocIds = collectDatabaseStorageSyncDocIds;
exports.createLocalDatabaseRootDocId = createLocalDatabaseRootDocId;
exports.parseServerDatabaseRowShardDocId = parseServerDatabaseRowShardDocId;
exports.parseServerDatabaseRootDocId = parseServerDatabaseRootDocId;
exports.parseLocalDatabaseRootDocId = parseLocalDatabaseRootDocId;
exports.extractDatabaseIdFromRootDocId = extractDatabaseIdFromRootDocId;
exports.normalizeDatabaseBlockId = normalizeDatabaseBlockId;
exports.isServerDatabaseRowShardDocId = isServerDatabaseRowShardDocId;
exports.isServerDatabaseRootDocId = isServerDatabaseRootDocId;
exports.isLocalDatabaseRootDocId = isLocalDatabaseRootDocId;
function assertNonEmptyIdentifier(value, label) {
    if (!value || value.includes('$')) {
        throw new Error(`${label} must be a non-empty identifier without "$"`);
    }
}
function createServerDatabaseRowShardDocId({ workspaceId, databaseId, shardId, }) {
    assertNonEmptyIdentifier(workspaceId, 'Workspace id');
    assertNonEmptyIdentifier(databaseId, 'Database id');
    assertNonEmptyIdentifier(shardId, 'Database row-shard id');
    return `db$${workspaceId}$database$${databaseId}$rows$${shardId}`;
}
function createServerDatabaseRootDocId({ workspaceId, databaseId, }) {
    assertNonEmptyIdentifier(workspaceId, 'Workspace id');
    assertNonEmptyIdentifier(databaseId, 'Database id');
    return `db$${workspaceId}$database$${databaseId}$root`;
}
/**
 * Database system documents required before a server-side database operation.
 * Known storage docs win; a fresh database deterministically falls back to its
 * root and first row shard once a workspace id is available.
 */
function collectDatabaseStorageSyncDocIds({ databaseId, databaseRootDocId, databaseRowShardDocIds, workspaceId, }) {
    const docIds = new Set();
    const add = (docId) => {
        if (docId)
            docIds.add(docId);
    };
    add(databaseRootDocId ??
        (workspaceId
            ? createServerDatabaseRootDocId({ workspaceId, databaseId })
            : undefined));
    const rowShardDocIds = databaseRowShardDocIds?.filter((docId) => typeof docId === 'string' && docId.length > 0);
    if (rowShardDocIds?.length) {
        rowShardDocIds.forEach(add);
    }
    else if (workspaceId) {
        add(createServerDatabaseRowShardDocId({
            workspaceId,
            databaseId,
            shardId: '00001',
        }));
    }
    return [...docIds];
}
function createLocalDatabaseRootDocId({ databaseId, }) {
    assertNonEmptyIdentifier(databaseId, 'Database id');
    return `db$database$${databaseId}$root`;
}
function parseServerDatabaseRowShardDocId(docId, workspaceId) {
    const prefix = `db$${workspaceId}$database$`;
    if (!docId.startsWith(prefix)) {
        return null;
    }
    const suffix = docId.slice(prefix.length);
    const parts = suffix.split('$');
    if (parts.length !== 3 || parts[1] !== 'rows') {
        return null;
    }
    const [databaseId, , shardId] = parts;
    if (!databaseId || !shardId) {
        return null;
    }
    return { workspaceId, databaseId, shardId };
}
function parseServerDatabaseRootDocId(docId, workspaceId) {
    const prefix = `db$${workspaceId}$database$`;
    if (!docId.startsWith(prefix)) {
        return null;
    }
    const suffix = docId.slice(prefix.length);
    const parts = suffix.split('$');
    if (parts.length !== 2 || parts[1] !== 'root') {
        return null;
    }
    const [databaseId] = parts;
    if (!databaseId) {
        return null;
    }
    return { workspaceId, databaseId };
}
function parseLocalDatabaseRootDocId(docId) {
    const prefix = 'db$database$';
    if (!docId.startsWith(prefix)) {
        return null;
    }
    const suffix = docId.slice(prefix.length);
    const parts = suffix.split('$');
    if (parts.length !== 2 || parts[1] !== 'root') {
        return null;
    }
    const [databaseId] = parts;
    if (!databaseId) {
        return null;
    }
    return { databaseId };
}
function extractDatabaseIdFromRootDocId(docId) {
    const local = parseLocalDatabaseRootDocId(docId);
    if (local)
        return local.databaseId;
    const parts = docId.split('$');
    if (parts.length === 5 &&
        parts[0] === 'db' &&
        parts[2] === 'database' &&
        parts[4] === 'root') {
        const databaseId = parts[3];
        return databaseId ? databaseId : null;
    }
    return null;
}
function normalizeDatabaseBlockId(value) {
    return extractDatabaseIdFromRootDocId(value) ?? value;
}
function isServerDatabaseRowShardDocId(docId, workspaceId) {
    return parseServerDatabaseRowShardDocId(docId, workspaceId) !== null;
}
function isServerDatabaseRootDocId(docId, workspaceId) {
    return parseServerDatabaseRootDocId(docId, workspaceId) !== null;
}
function isLocalDatabaseRootDocId(docId) {
    return parseLocalDatabaseRootDocId(docId) !== null;
}
